import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';

const TransactionsComponent = () => {
  const [transactions, setTransactions] = useState([]);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({
    to_user_email: '',
    amount: '',
    currency: 'CUALCOIN',
    description: ''
  });
  const [currencies, setCurrencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
    fetchCurrencies();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions/history`);
      setTransactions(response.data.transactions);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/wallet/currencies`);
      setCurrencies(response.data);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    }
  };

  const handleSendMoney = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    try {
      await axios.post(`${API}/transactions/send`, {
        to_user_email: sendForm.to_user_email,
        amount: parseFloat(sendForm.amount),
        currency: sendForm.currency,
        description: sendForm.description || null
      });

      // Reset form and refresh data
      setSendForm({ to_user_email: '', amount: '', currency: 'CUALCOIN', description: '' });
      setShowSendForm(false);
      fetchTransactions();
      
      alert('¡Dinero enviado exitosamente!');
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al enviar dinero');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Transacciones CUALAPP</h2>
          <p className="text-gray-600">Historial de envíos y recepciones</p>
        </div>
        <button
          onClick={() => setShowSendForm(!showSendForm)}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200"
        >
          {showSendForm ? 'Cancelar' : 'Enviar Dinero'}
        </button>
      </div>

      {showSendForm && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-purple-100">
          <h3 className="text-lg font-semibold mb-4">Enviar Dinero</h3>
          <form onSubmit={handleSendMoney} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email del Destinatario</label>
              <input
                type="email"
                required
                value={sendForm.to_user_email}
                onChange={(e) => setSendForm({...sendForm, to_user_email: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Cantidad</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={sendForm.amount}
                  onChange={(e) => setSendForm({...sendForm, amount: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Moneda</label>
                <select
                  value={sendForm.currency}
                  onChange={(e) => setSendForm({...sendForm, currency: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                >
                  {currencies && currencies.native_currency && (
                    <option value={currencies.native_currency.code}>
                      {currencies.native_currency.symbol} {currencies.native_currency.name} (Nativa)
                    </option>
                  )}
                  {currencies && currencies.credit_unit && (
                    <option value={currencies.credit_unit.code}>
                      {currencies.credit_unit.symbol} {currencies.credit_unit.name} (Crédito Estable)
                    </option>
                  )}
                  <optgroup label="CUAL-Fiat">
                    {currencies && currencies.fiat_currencies?.map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="CUAL-Crypto">
                    {currencies && currencies.cryptocurrencies?.map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Descripción (Opcional)</label>
              <input
                type="text"
                value={sendForm.description}
                onChange={(e) => setSendForm({...sendForm, description: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Motivo del envío..."
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 rounded-md hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-all duration-200"
            >
              {sending ? 'Enviando...' : 'Enviar Dinero'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Historial de Transacciones</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {transactions.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No hay transacciones aún
            </div>
          ) : (
            transactions.map((tx) => (
              <div key={tx.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        tx.type === 'sent' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {tx.type === 'sent' ? 'Enviado' : 'Recibido'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {tx.type === 'sent' ? 'a' : 'de'} {tx.other_party}
                      </span>
                      {tx.currency === 'CUAL' && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          CUAL Coin
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{tx.description || 'Sin descripción'}</p>
                    <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-semibold ${
                      tx.type === 'sent' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {tx.type === 'sent' ? '-' : '+'} {tx.amount} {tx.currency}
                    </p>
                    <p className={`text-xs capitalize ${
                      tx.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {tx.status === 'completed' ? 'Completado' : 'Pendiente'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Domains Component - Mind Map View


export default TransactionsComponent;
